import { type SetRequired } from 'type-fest';

import {
  type BulkActionsProps,
  type CanvasElement,
  type CanvasElementProperties,
  type CanvasElementType,
  type DataProps,
  type FilterBuilderProps,
  type HasDataProps,
  type HasDirtyFieldProps,
  type HasProps,
  type ParentStyledElementProps,
  type ParentStyleSource,
  type PartialPropsOf,
  type RowClickActionProps,
  type SearchAndFiltersProps,
  type SlotData,
  type SlotId,
  type SlotIdOf,
  type SlotNodeData,
  type StyleConfigProps,
  type TableGroupByProps,
  type TitlePropsModel,
  type TransformsElementProps,
  type WithProps,
  type WithSlots,
  type WrappedElementProps,
  type WrapperElementProps,
} from '@blaze-runtime/engine-domain';
import {
  type CanvasElementWithDirtyFieldProps,
  type CanvasElementWithFeaturesConfigProps,
  type ComponentFeatureConditionsModel,
  type ElementLabelProps,
  type ElementRedirect,
  type RowClickAction,
  STYLE_CONFIG_PROPERTIES_PATH,
  type WithBulkActions,
} from '@blaze-shared/elements-domain';

import { isElementType, isVariantOfContainer } from './element-type-guards';

// TODO: test
export function hasProps<T extends CanvasElementProperties>(
  element: CanvasElement | null | undefined,
): element is CanvasElement & WithProps<T> {
  return element !== undefined && element !== null && 'props' in element;
}

// TODO: test
export function hasDataProps(
  element: CanvasElement | undefined,
): element is CanvasElement & HasDataProps {
  return element !== undefined && 'props' in element && 'data' in element.props;
}

export function hasDirtyFieldProps(
  element: CanvasElement | undefined,
): element is CanvasElement & HasDirtyFieldProps {
  return element !== undefined && 'props' in element && 'dirty' in element.props;
}

export function arePropsWithData<T extends PartialPropsOf<CanvasElementType>>(
  props: T | undefined,
): props is T & DataProps {
  return props !== null && typeof props === 'object' && 'data' in props;
}

export function arePropsWithDirty<T extends PartialPropsOf<CanvasElementType>>(
  props: T | undefined,
): props is T & CanvasElementWithDirtyFieldProps {
  return props !== null && typeof props === 'object' && 'dirty' in props;
}

export function arePropsWithFeaturesConfig<T extends PartialPropsOf<CanvasElementType>>(
  props: T | undefined,
): props is T & CanvasElementWithFeaturesConfigProps {
  return props !== null && typeof props === 'object' && 'featuresConfig' in props;
}

export function arePropsWithWrapper<T extends PartialPropsOf<CanvasElementType>>(
  props: T | undefined,
): props is T & WrapperElementProps {
  return !!props && 'wrapper' in props;
}

export function arePropsWithWrapperRefs<T extends PartialPropsOf<CanvasElementType>>(
  props: T | undefined,
): props is T & WrappedElementProps {
  return arePropsWithWrapper(props) && !!props.wrapper && 'refs' in props.wrapper;
}

/** Elements exposing the standard title block (label + icon) under `content.title`. */
export function arePropsWithTitle(
  element?: CanvasElement,
): element is CanvasElement & HasProps<TitlePropsModel> {
  return arePropsWithContent(element) && 'title' in element.props.content;
}

export function arePropsWithContent(
  element?: CanvasElement,
): element is CanvasElement & HasProps<{ content: Record<string, unknown> }> {
  return (
    hasProps(element) &&
    'content' in element.props &&
    typeof element.props.content === 'object' &&
    element.props.content !== null
  );
}

export function arePropsWithSlots<T>(props: T | undefined): props is T & WithSlots<any> {
  return !!props && typeof props === 'object' && 'slots' in props;
}

// TODO: test
export function slotHasProps(slot: SlotData): slot is SetRequired<SlotData, 'props'> {
  return !!slot.props;
}

// TODO: test
export function hasSlots<T extends string>(
  element?: CanvasElement,
): element is CanvasElement & WithSlots<T> {
  return element !== undefined && element.slots !== undefined;
}

export function isSlotIdOfContainer(
  element?: CanvasElement,
  slotId?: SlotId,
): slotId is SlotIdOf<'container'> {
  return isVariantOfContainer(element) && !!slotId && /^\d+$/.test(slotId);
}

// TODO: test
export function isWrapper(
  element?: CanvasElement,
): element is CanvasElement & HasProps<Required<WrapperElementProps>> {
  return hasProps(element) && 'wrapper' in element.props;
}

// TODO: test
export function hasWrapperRefs(
  element?: CanvasElement,
): element is CanvasElement & HasProps<Required<WrappedElementProps>> {
  return isWrapper(element) && 'refs' in element.props.wrapper;
}

/**
 * The parent-styling back-pointer of a generated child ("Style All Fields" family).
 * Only generated children carry the marker, so it is narrowed off the props at hand rather
 * than declared on every element - absence means the child is not styled by a parent.
 */
export function getParentStyleSource(
  props: CanvasElementProperties | null | undefined,
): ParentStyleSource | undefined {
  return props && hasParentStyleSource(props) ? props.parentStyleSource : undefined;
}

function hasParentStyleSource(
  props: CanvasElementProperties,
): props is SetRequired<ParentStyledElementProps, 'parentStyleSource'> {
  return (
    'parentStyleSource' in props &&
    typeof props.parentStyleSource === 'object' &&
    props.parentStyleSource !== null &&
    'elementId' in props.parentStyleSource
  );
}

// TODO: test
export function hasSearchAndFilters(
  element?: CanvasElement,
): element is CanvasElement & HasProps<SearchAndFiltersProps> {
  return (
    hasProps(element) &&
    'content' in element.props &&
    typeof element.props.content === 'object' &&
    element.props.content !== null &&
    'searchAndFilters' in element.props.content
  );
}

export function hasFilterBuilderConfig(
  element?: CanvasElement,
): element is CanvasElement & HasProps<FilterBuilderProps> {
  return (
    hasProps(element) &&
    'content' in element.props &&
    typeof element.props.content === 'object' &&
    element.props.content !== null &&
    'filterBuilderConfig' in element.props.content
  );
}

export function hasTableGroupBy(
  element?: CanvasElement,
): element is CanvasElement & HasProps<TableGroupByProps> {
  return (
    isElementType(element, 'table') &&
    !!(element.props.content?.table && 'groupBy' in element.props.content.table)
  );
}

// TODO: test
export function hasTransformHistory(
  element?: CanvasElement,
): element is CanvasElement & HasProps<Required<TransformsElementProps>> {
  return hasProps(element) && 'transforms' in element.props;
}

export function hasRowClick(
  element?: CanvasElement,
): element is CanvasElement & HasProps<Required<RowClickActionProps>> {
  return (
    hasProps(element) &&
    'content' in element.props &&
    typeof element.props.content === 'object' &&
    element.props.content !== null &&
    'rowClick' in element.props.content
  );
}

export function hasVisibilityConditions(
  obj: any,
): obj is { visibilityConditions: ComponentFeatureConditionsModel } {
  return !!obj && 'visibilityConditions' in obj && typeof obj.visibilityConditions === 'object';
}

export function isElementRedirect(element?: unknown): element is ElementRedirect {
  return !!element && typeof element === 'object' && 'enabled' in element;
}

export function hasStylesConfig(
  element?: CanvasElement,
): element is CanvasElement & HasProps<StyleConfigProps> {
  return hasProps(element) && STYLE_CONFIG_PROPERTIES_PATH in element.props;
}

export function hasSlotStyleConfig(slot?: SlotData): slot is SlotData & HasProps<StyleConfigProps> {
  return !!slot && slotHasProps(slot) && STYLE_CONFIG_PROPERTIES_PATH in slot.props;
}

export function hasSlotNodeStyleConfig(
  slotNode?: SlotNodeData,
): slotNode is SlotNodeData & HasProps<StyleConfigProps> {
  return !!slotNode?.props && STYLE_CONFIG_PROPERTIES_PATH in slotNode.props;
}

export function isRowClickAction(element?: unknown): element is RowClickAction['rowClick'] {
  return (
    !!element &&
    typeof element === 'object' &&
    'enabled' in element &&
    'action' in element &&
    'targetId' in element
  );
}

export function hasBulkAction<K extends keyof Omit<WithBulkActions, 'bulkActions'>>(
  element: CanvasElement | undefined,
  action: K,
): element is CanvasElement &
  HasProps<BulkActionsProps> & {
    props: {
      content: WithBulkActions & Required<Pick<WithBulkActions, K>>;
    };
  } {
  const props = (element as Partial<HasProps<BulkActionsProps>>)?.props;
  const content = props?.content as Partial<WithBulkActions> | undefined;

  return !!content?.[action];
}

export function areContentPropsWithTitle(
  element?: CanvasElement,
): element is CanvasElement & HasProps<{ content: { title: ElementLabelProps } }> {
  return (
    arePropsWithContent(element) &&
    'title' in element.props.content &&
    typeof element.props.content.title === 'object' &&
    element.props.content.title !== null
  );
}
